import React, { useState } from "react";
import { Link, useSearchParams } from "@/i18n/router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Search, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Stagger, staggerItem } from "@/components/motion/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const { lang } = useLang();
  const category = params.get("category") || "all";
  const page = parseInt(params.get("page") || "1", 10);
  const [search, setSearch] = useState(params.get("q") || "");

  const { data: cats } = useQuery({
    queryKey: ["blog-cats"],
    queryFn: async () => (await api.get("/blog/categories")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["blog-posts", category, page, params.get("q"), lang],
    queryFn: async () => {
      const build = (withLang) => {
        const q = new URLSearchParams();
        if (category !== "all") q.set("category", category);
        if (params.get("q")) q.set("q", params.get("q"));
        if (withLang) q.set("language", lang);
        q.set("page", page);
        q.set("limit", "9");
        return q.toString();
      };
      let res = (await api.get(`/blog/posts?${build(true)}`)).data;
      // Fallback to all languages if the current language has no articles yet
      if (res.total === 0) res = (await api.get(`/blog/posts?${build(false)}`)).data;
      return res;
    },
  });

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setParam("q", search);
  };

  const posts = data?.posts || [];
  const pages = data?.pages || 1;

  return (
    <>
      <Seo
        title="Blog — AI Governance, EU AI Act & Digital Sovereignty"
        description="Insights on AI governance, the EU AI Act, cybersecurity, digital sovereignty and trustworthy AI from the AstroLife editorial team."
        path="/blog"
        keywords="AI Governance blog, EU AI Act, Digital Sovereignty, Cybersecurity, Responsible AI, Trustworthy AI"
      />

      <PageHero
        overline="Journal"
        title="Insights on verifiable AI governance."
        subtitle="Perspectives on AI governance, the EU AI Act, cybersecurity, digital sovereignty and the engineering of trustworthy artificial intelligence."
        breadcrumbs={[{ label: "Blog" }]}
      />

      <section className="bg-navy py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {/* Filters */}
          <div className="flex flex-col gap-6 border-b border-navy-border pb-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setParam("category", "all")}
                data-testid="blog-cat-all"
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === "all" ? "bg-astro-blue text-white" : "border border-navy-border text-silver/70 hover:text-white"}`}
              >
                All
              </button>
              {(cats || []).map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setParam("category", c.name)}
                  data-testid={`blog-cat-${c.slug}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c.name ? "bg-astro-blue text-white" : "border border-navy-border text-silver/70 hover:text-white"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <form onSubmit={onSearch} className="relative w-full lg:w-72">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-silver/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles"
                data-testid="blog-search-input"
                className="w-full rounded-full border border-navy-border bg-navy-surface py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none"
              />
            </form>
          </div>

          {/* Posts */}
          {isLoading ? (
            <div className="py-24 text-center text-silver/50">Loading articles…</div>
          ) : posts.length === 0 ? (
            <div className="py-24 text-center text-silver/50" data-testid="blog-empty">No articles found.</div>
          ) : (
            <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <motion.article key={p.id} variants={staggerItem}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy-border bg-navy-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-astro-blue/40">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-xs font-semibold uppercase tracking-wider text-astro-light">{p.category}</span>
                      <h2 className="mt-3 font-heading text-lg font-semibold leading-snug text-white group-hover:text-astro-light transition-colors">{p.title}</h2>
                      <p className="mt-2 line-clamp-3 flex-1 text-sm text-silver/60">{p.excerpt}</p>
                      <div className="mt-4 flex items-center gap-3 text-xs text-silver/45">
                        <span>{p.author_name}</span>
                        {p.reading_time && <span>· {p.reading_time} min read</span>}
                      </div>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-astro-light">
                        Read article <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </Stagger>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="mt-14 flex items-center justify-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setParam("page", String(page - 1))}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy-border text-silver/70 disabled:opacity-30 hover:text-white transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setParam("page", String(n))}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium transition-colors ${n === page ? "bg-astro-blue text-white" : "border border-navy-border text-silver/70 hover:text-white"}`}
                >
                  {n}
                </button>
              ))}
              <button
                disabled={page >= pages}
                onClick={() => setParam("page", String(page + 1))}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-navy-border text-silver/70 disabled:opacity-30 hover:text-white transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
